import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import API_BASE_URL from '../config/api';

interface PaymentModalProps {
  planType: 'BUSINESS' | 'ENTERPRISE';
  billingCycle: 'MONTHLY' | 'YEARLY';
  onClose: () => void;
  onSuccess: () => void;
  onError: (error: string) => void;
}

// 🆕 NEW: Interface for dynamic plan configuration
interface PlanConfiguration {
  id: string;
  planType: 'FREE' | 'BUSINESS' | 'ENTERPRISE';
  name: string;
  description: string;
  features: string[];
  monthlyPrice: number;
  yearlyPrice: number;
  apiRequestsLimit: number | null;
  tokensLimit: number | null;
  isActive: boolean;
  popularBadge: boolean;
  savings: string | null;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  planType,
  billingCycle,
  onClose,
  onSuccess,
  onError
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [cardError, setCardError] = useState('');
  const [planConfig, setPlanConfig] = useState<PlanConfiguration | null>(null);
  const [loadingPlan, setLoadingPlan] = useState(true);

  // 🆕 NEW: Load plan configuration on mount
  useEffect(() => {
    loadPlanConfiguration();
  }, [planType]);

  const loadPlanConfiguration = async () => {
    try {
      setLoadingPlan(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/subscription/plans/${planType}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        setPlanConfig(data.data);
      } else {
        console.error('Failed to load plan configuration:', data.message);
        // 🔄 FALLBACK: Use default values if API fails
        setPlanConfig({
          id: '',
          planType,
          name: planType === 'BUSINESS' ? 'Business Plan' : 'Enterprise Plan',
          description: '',
          features: [],
          monthlyPrice: planType === 'BUSINESS' ? 2900 : 19900, // $29 or $199 in cents
          yearlyPrice: planType === 'BUSINESS' ? 28800 : 199200, // with savings
          apiRequestsLimit: planType === 'BUSINESS' ? 10000 : null,
          tokensLimit: planType === 'BUSINESS' ? 1000000 : null,
          isActive: true,
          popularBadge: false,
          savings: null
        });
      }
    } catch (error) {
      console.error('Error loading plan configuration:', error);
      // Use fallback configuration
      setPlanConfig({
        id: '',
        planType,
        name: planType === 'BUSINESS' ? 'Business Plan' : 'Enterprise Plan',
        description: '',
        features: [],
        monthlyPrice: planType === 'BUSINESS' ? 2900 : 19900,
        yearlyPrice: planType === 'BUSINESS' ? 28800 : 199200,
        apiRequestsLimit: planType === 'BUSINESS' ? 10000 : null,
        tokensLimit: planType === 'BUSINESS' ? 1000000 : null,
        isActive: true,
        popularBadge: false,
        savings: null
      });
    } finally {
      setLoadingPlan(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setCardError('');

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setCardError('Card element not found');
      setLoading(false);
      return;
    }

    // Create payment method
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    if (error) {
      setCardError(error.message || 'An error occurred');
      setLoading(false);
      return;
    }

    try {
      // Call your backend to create subscription
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          planType,
          billingCycle,
          paymentMethodId: paymentMethod.id
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // If the subscription requires additional authentication (3D Secure)
        if (data.clientSecret) {
          console.log('Confirming payment with client secret:', data.clientSecret);
          const { error: confirmError } = await stripe.confirmCardPayment(data.clientSecret);
          
          if (confirmError) {
            console.error('Payment confirmation failed:', confirmError);
            onError(confirmError.message || 'Payment failed');
          } else {
            console.log('Payment confirmed successfully');
            // Wait a moment for Stripe to update the subscription status
            setTimeout(() => {
              onSuccess();
            }, 1000);
          }
        } else {
          console.log('No client secret - subscription created successfully');
          onSuccess();
        }
      } else {
        console.error('Backend error:', data);
        onError(data.message || 'Failed to create subscription');
      }
    } catch (error) {
      console.error('Payment error:', error);
      onError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (num: number | null): string => {
    if (num === null || num === undefined) return "Unlimited";
    return num.toLocaleString();
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  // Show loading spinner while plan configuration loads
  if (loadingPlan) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-gray-600">Loading plan details...</span>
          </div>
        </div>
      </div>
    );
  }

  // If plan configuration failed to load
  if (!planConfig) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-red-600 mb-2">Error</h3>
            <p className="text-gray-600 mb-4">Failed to load plan configuration. Please try again.</p>
            <button
              onClick={onClose}
              className="bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Upgrade to {planConfig.name}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {/* 🔄 UPDATED: Plan details now use dynamic configuration */}
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium">{planConfig.name}</span>
            <span className="font-bold text-lg">
              {formatCurrency(
                billingCycle === 'YEARLY' ? planConfig.yearlyPrice : planConfig.monthlyPrice
              )}
              <span className="text-sm text-gray-600">
                /{billingCycle === 'YEARLY' ? 'year' : 'month'}
              </span>
            </span>
          </div>
          
          {planConfig.description && (
            <p className="text-sm text-gray-600 mb-3">{planConfig.description}</p>
          )}

          {/* 🆕 NEW: Show plan limits */}
          <div className="text-sm text-gray-600 mb-3">
            <div>API Requests: {formatNumber(planConfig.apiRequestsLimit)} per month</div>
            <div>Tokens: {formatNumber(planConfig.tokensLimit)} per month</div>
          </div>

          {billingCycle === 'YEARLY' && planConfig.savings && (
            <div className="text-sm text-green-600 font-medium">{planConfig.savings}</div>
          )}
        </div>

        {/* 🆕 NEW: Show plan features if available */}
        {planConfig.features && planConfig.features.length > 0 && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">What's included:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              {planConfig.features.slice(0, 4).map((feature, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Information
            </label>
            <div className="border border-gray-300 rounded-lg p-3">
              <CardElement options={cardElementOptions} />
            </div>
            {cardError && (
              <p className="text-red-600 text-sm mt-2">{cardError}</p>
            )}
          </div>

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!stripe || loading}
              className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Processing...' : `Subscribe for ${formatCurrency(
                billingCycle === 'YEARLY' ? planConfig.yearlyPrice : planConfig.monthlyPrice
              )}`}
            </button>
          </div>
        </form>

        <p className="text-xs text-gray-500 mt-4 text-center">
          Your payment information is secure and encrypted. 
          You can cancel your subscription at any time.
        </p>
      </div>
    </div>
  );
};

export default PaymentModal;